import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ActivationModal } from "@/components/substitution/activation-modal";
import { TerminationModal } from "@/components/substitution/termination-modal";
import { useAuthData } from "@/context/auth-data-provider";
import { FALLBACK_REGIONAL_LABEL } from "@/context/utils/substitution-format";
import { formatDate } from "@/utils/format-date";
import { PATH_IDS } from "@/config/routes/path-ids";
import { useReconcile } from "@/hooks/use-reconcile";
import {
  SubstitutionState,
  SubstitutionStates,
  useSubstitutionState,
} from "@/hooks/use-substitution-state";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";

const ACTIVATION_TITLE = "Ativar substituição";
const TERMINATION_TITLE = "Substituição encerrada";
const HOME_PATH = "/";

type TransitionPolicy = () => void;
type TransitionMap = Partial<
  Record<SubstitutionState, Partial<Record<SubstitutionState, TransitionPolicy>>>
>;

export function SubstitutionWatcher() {
  const authData = useAuthData();
  const state = useSubstitutionState();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleModal } = useDialogModal();
  const reconcile = useReconcile();

  const entityName = authData?.substitution?.entity?.name;
  const dataFim = authData?.substitution?.period?.end;

  const prevStateRef = useRef<SubstitutionState>(SubstitutionStates.NONE);
  const everActivatedRef = useRef<boolean>(false);
  const lastRegionalNameRef = useRef<string>(FALLBACK_REGIONAL_LABEL);
  const lastDataFimRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (state !== SubstitutionStates.NONE && entityName) {
      lastRegionalNameRef.current = entityName;
    }
    if (state !== SubstitutionStates.NONE && dataFim) {
      lastDataFimRef.current = dataFim;
    }
    if (state === SubstitutionStates.ACTIVE) {
      everActivatedRef.current = true;
    }

    const prev = prevStateRef.current;
    prevStateRef.current = state;

    const openActivation: TransitionPolicy = () => {
      const activateAndReconcile = (): void => {
        handleModal({ isOpen: false });
        void reconcile();
      };

      handleModal({
        isOpen: true,
        title: ACTIVATION_TITLE,
        onClose: () => {
          void reconcile();
        },
        element: (
          <ActivationModal
            regionalName={lastRegionalNameRef.current}
            dataFim={formatDate(lastDataFimRef.current, { fallback: "" })}
            onConfirm={activateAndReconcile}
          />
        ),
      });
    };

    const openTermination: TransitionPolicy = () => {
      const everActivated = everActivatedRef.current;
      const regionalName = lastRegionalNameRef.current;
      everActivatedRef.current = false;

      const wasOnHerdedScreen = location.pathname.startsWith(
        PATH_IDS.RETAGUARDA_DE_LOJA,
      );

      const closeAndReconcile = (): void => {
        handleModal({ isOpen: false });
        void reconcile();
        if (wasOnHerdedScreen) {
          navigate(HOME_PATH, { replace: true });
        }
      };

      handleModal({
        isOpen: true,
        title: TERMINATION_TITLE,
        onClose: closeAndReconcile,
        element: (
          <TerminationModal
            regionalName={regionalName}
            everActivated={everActivated}
            onConfirm={closeAndReconcile}
          />
        ),
      });
    };

    const transitions: TransitionMap = {
      [SubstitutionStates.NONE]: {
        [SubstitutionStates.PENDING]: openActivation,
      },
      [SubstitutionStates.ACTIVE]: {
        [SubstitutionStates.PENDING]: openActivation,
        [SubstitutionStates.NONE]: openTermination,
      },
      [SubstitutionStates.PENDING]: {
        [SubstitutionStates.NONE]: openTermination,
      },
    };

    transitions[prev]?.[state]?.();
  }, [
    state,
    entityName,
    dataFim,
    location.pathname,
    handleModal,
    navigate,
    reconcile,
  ]);

  return null;
}
